import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { useConfigValue } from '~/shared/utils/config';
import { FormattedMessage } from '~/techstyle-shared/react-intl/index.js';

import useSelectedFacetsToApply from '../../hooks/useSelectedFacetsToApply';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';
import {
  getSelectedFacetRbid,
  sanitizeFilterName,
  generateFilterImageUrl,
} from '../../utils/filters';
import ImageSwatch from '../SharedGridFiltersUi/ImageSwatch';

const DynamicFormattedMessage = FormattedMessage;

const MobileGridImageFilterFieldset = styled.fieldset`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
  border: none;
`;

const HiddenLegend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px;
  border-radius: 9999px;
  cursor: pointer;
  ${({ theme }) => theme.gridFilterStyles.colorFilterStyle};

  ${({ isSelected }) =>
    isSelected
      ? css`
          background-color: black;
          color: white;
          border: 1px solid white;
        `
      : css`
          background-color: ${({ theme }) =>
            theme.colors.gridSelectedFiltersPillBackgroundColor};
          color: ${({ theme }) => theme.colors.palette.shadow90};
        `}
`;

// eslint-disable-next-line camelcase
const MobileGridImageFilter = ({ display_name, options, name }) => {
  const { selectedUrlFilters } = useUnifiedProductGridData();
  const { toggleSelectedFacetsToApply, isFacetOptionSelected } =
    useSelectedFacetsToApply();
  const sanitizedName = sanitizeFilterName(name);
  const filterImageCdnUrl = useConfigValue('public.filterImageCdnUrl');

  const handleOnChange = ({ facetName, facetOption }) => {
    toggleSelectedFacetsToApply({
      facetName,
      facetOption,
    });
  };

  return (
    <MobileGridImageFilterFieldset aria-labelledby={`${name}-legend`}>
      {/* eslint-disable-next-line camelcase */}
      <HiddenLegend id={`${name}-legend`}>{display_name}</HiddenLegend>

      {options.map((option) => {
        const inputId = `${name}-${option.value}`;
        const isSelected = isFacetOptionSelected({
          facetName: name,
          facetOption: option.value,
          selectedUrlFilters,
        });

        const imageUrl = generateFilterImageUrl(
          name,
          option.value,
          filterImageCdnUrl
        );

        return (
          <React.Fragment key={option.value}>
            <HiddenCheckbox
              id={inputId}
              type="checkbox"
              name={name}
              checked={isSelected}
              onChange={() =>
                handleOnChange({
                  facetName: name,
                  facetOption: option.value,
                })
              }
            />
            <Label
              htmlFor={inputId}
              role="checkbox"
              aria-checked={isSelected}
              isSelected={isSelected}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  const input = document.getElementById(inputId);
                  if (input) {
                    input.click();
                  }
                }
              }}
              data-autotag={`filter-${sanitizedName}-option`}
            >
              <ImageSwatch imageUrl={imageUrl} aria-hidden="true" />
              <DynamicFormattedMessage
                id={`site_product_filter.${getSelectedFacetRbid(
                  name,
                  option.display_name
                )}`}
                defaultMessage={option.display_name}
              />
            </Label>
          </React.Fragment>
        );
      })}
    </MobileGridImageFilterFieldset>
  );
};

MobileGridImageFilter.propTypes = {
  display_name: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default MobileGridImageFilter;
